import moment from "moment";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

const BANK = {
  name: "FNB",
  branchCode: "250655",
  accountNumber: "62852587426",
};

function parseAmount(value) {
  if (value === null || value === undefined) return 0;
  const str = (value + "").trim();
  const trailingNeg = /-$/.test(str);
  const cleaned = str.replace(/[^0-9.-]+/g, "").replace(/-/g, "");
  const num = parseFloat(cleaned) || 0;
  return trailingNeg ? -num : /^-/.test(str) ? -num : num;
}

/** Invoice amount in rands (billing API returns `amount` already in rands). */
export function getTripAmountRands(invoice) {
  if (invoice.amount !== undefined && invoice.amount !== null) {
    return parseAmount(invoice.amount);
  }
  if (invoice.sellingprice !== undefined && invoice.sellingprice !== null) {
    return parseAmount(invoice.sellingprice) / 100;
  }
  return 0;
}

function getTripBreakdown(invoice) {
  const gross = getTripAmountRands(invoice);
  const servicefee =
    typeof invoice?.servicefee === "number" ? invoice.servicefee : 0.2;
  const fee = gross * servicefee;
  const commission = fee / 1.15;
  const vat = commission * 0.15;
  return { gross, commission, vat };
}

function fmtNum(v) {
  return Number(v).toLocaleString("en-ZA", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function escapeCsv(val) {
  const s = String(val ?? "");
  return s.includes(",") || s.includes('"') || s.includes("\n")
    ? `"${s.replace(/"/g, '""')}"`
    : s;
}

function triggerDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

function slugPeriod(periodLabel) {
  return String(periodLabel || moment().format("MMMM YYYY"))
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9-]/g, "");
}

/**
 * Download a CSV statement for the given billing invoices (HQ-compatible breakdown).
 */
export function downloadBillingCSV(invoices, options = {}) {
  const list = invoices || [];
  if (!list.length) return false;

  const accountFee = Number(options.accountFee) || 0;
  const accountFeeVat = accountFee * 0.15;
  const periodLabel = options.periodLabel || moment().format("MMMM YYYY");
  const promocode = options.promocode || "customer";

  const rows = list.map((inv) => {
    const b = getTripBreakdown(inv);
    return {
      Date: inv.date ? moment(inv.date).format("YYYY-MM-DD") : "",
      "Order ID": inv.id || inv.orderid || "",
      Status: inv.status || inv.paymentstatus || "",
      "Vehicle Type": inv.trucktype || "",
      "Collection Address": inv.collectionaddress || "",
      "Delivery Address": inv.deliveryaddress || "",
      "Selling Price (R)": b.gross.toFixed(2),
      "Service Fee (R)": b.commission.toFixed(2),
      "VAT (R)": b.vat.toFixed(2),
      Promo: inv.promocode || "",
    };
  });

  const tripsSubtotal = rows.reduce(
    (s, r) => s + (parseFloat(r["Selling Price (R)"]) || 0),
    0
  );
  const serviceFeeSubtotal = rows.reduce(
    (s, r) => s + (parseFloat(r["Service Fee (R)"]) || 0),
    0
  );
  const vatInTrips = rows.reduce((s, r) => s + (parseFloat(r["VAT (R)"]) || 0), 0);
  const totalVat = vatInTrips + accountFeeVat;
  const subTotalExclVat = tripsSubtotal - vatInTrips + accountFee;
  const invoiceTotal = subTotalExclVat + totalVat;

  const emptyRow = Object.fromEntries(Object.keys(rows[0]).map((k) => [k, ""]));
  const breakdownRows = [
    { ...emptyRow },
    {
      ...emptyRow,
      Date: "Trips Subtotal (incl. VAT on service fee)",
      "Selling Price (R)": tripsSubtotal.toFixed(2),
    },
    {
      ...emptyRow,
      Date: "  Service Fee (Logistics Administration, incl. in trips)",
      "Selling Price (R)": serviceFeeSubtotal.toFixed(2),
    },
    {
      ...emptyRow,
      Date: "Account Fee (SaaS)",
      "Selling Price (R)": accountFee.toFixed(2),
    },
    {
      ...emptyRow,
      Date: "Sub-total (excl. VAT)",
      "Selling Price (R)": subTotalExclVat.toFixed(2),
    },
    {
      ...emptyRow,
      Date: "Total VAT at 15%",
      "Selling Price (R)": totalVat.toFixed(2),
    },
    {
      ...emptyRow,
      Date: "  VAT on service fee (embedded in trips)",
      "Selling Price (R)": vatInTrips.toFixed(2),
    },
    {
      ...emptyRow,
      Date: `  VAT on account fee (15% of R${accountFee.toFixed(2)})`,
      "Selling Price (R)": accountFeeVat.toFixed(2),
    },
    {
      ...emptyRow,
      Date: "Invoice Amount",
      "Selling Price (R)": invoiceTotal.toFixed(2),
    },
  ];

  const headers = Object.keys(rows[0]);
  const csvContent = [
    headers.join(","),
    ...[...rows, ...breakdownRows].map((row) =>
      headers.map((h) => escapeCsv(row[h])).join(",")
    ),
  ].join("\n");

  triggerDownload(
    new Blob([csvContent], { type: "text/csv;charset=utf-8;" }),
    `statement-${promocode}-${slugPeriod(periodLabel)}.csv`
  );
  return true;
}

/**
 * Download a tax-invoice PDF for the given billing invoices (HQ-style layout).
 */
export function downloadBillingPDF(invoices, options = {}) {
  const list = invoices || [];
  if (!list.length) return false;
  const periodLabel = options.periodLabel || moment().format("MMMM YYYY");
  const promocode = options.promocode || "QT";
  const doc = buildBillingPDFDoc(list, options);
  if (!doc) return false;
  doc.save(`Invoice-${promocode}-${slugPeriod(periodLabel)}.pdf`);
  return true;
}

/** Build a PDF Blob for email upload (same layout as download). */
export function buildBillingPDFBlob(invoices, options = {}) {
  const doc = buildBillingPDFDoc(invoices || [], options);
  if (!doc) return null;
  return doc.output("blob");
}

function buildBillingPDFDoc(list, options = {}) {
  if (!list.length) return null;

  const accountFee = Number(options.accountFee) || 0;
  const accountFeeVat = accountFee * 0.15;
  const periodLabel = options.periodLabel || moment().format("MMMM YYYY");
  const promocode = options.promocode || "QT";
  const customerName = options.customerName || promocode;
  const email = options.email || "";
  const endDate =
    options.endDate ||
    list.reduce((max, inv) => {
      const t = moment(inv.date).valueOf();
      return t > max ? t : max;
    }, 0);

  const tripsSubtotal = list.reduce((s, inv) => s + getTripAmountRands(inv), 0);
  const serviceFeeSubtotal = list.reduce(
    (s, inv) => s + getTripBreakdown(inv).commission,
    0
  );
  const vatInTrips = list.reduce((s, inv) => s + getTripBreakdown(inv).vat, 0);
  const totalVat = vatInTrips + accountFeeVat;
  const subTotalExclVat = tripsSubtotal - vatInTrips + accountFee;
  const total = subTotalExclVat + totalVat;

  const doc = new jsPDF();
  const pw = doc.internal.pageSize.getWidth();
  const m = 15;
  const primary = [79, 99, 103];
  const yellow = [255, 215, 17];
  const darkText = [20, 20, 20];
  const mutedText = [110, 110, 110];
  const lightBg = [247, 248, 248];

  const invoiceDate = endDate
    ? moment(endDate).format("MMM DD, YYYY")
    : moment().format("MMM DD, YYYY");
  const invoiceNumber = `${String(promocode).toLowerCase()}.${moment(
    endDate || undefined
  )
    .format("MMMM")
    .toLowerCase()}.${moment(endDate || undefined).format("YYYY")}`;

  doc.setFillColor(...primary);
  doc.rect(0, 0, pw, 36, "F");
  doc.setFillColor(...yellow);
  doc.rect(0, 0, pw, 3, "F");

  doc.setFont("helvetica", "bold");
  doc.setFontSize(20);
  doc.setTextColor(...yellow);
  doc.text("QIKTRUCK", m, 19);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(255, 255, 255);
  const qtW = (doc.getStringUnitWidth("QIKTRUCK") * 20) / doc.internal.scaleFactor;
  doc.text("Logistics", m + qtW + 2, 19);

  doc.setFontSize(7.5);
  doc.setTextColor(180, 195, 195);
  doc.text("VAT Reg. No. 4700316260", m, 27);

  doc.setFont("helvetica", "bold");
  doc.setFontSize(13);
  doc.setTextColor(...yellow);
  doc.text("TAX INVOICE", pw - m, 18, { align: "right" });

  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(200, 210, 210);
  doc.text(`${invoiceNumber}  |  ${invoiceDate}`, pw - m, 27, { align: "right" });

  let y = 44;
  doc.setFontSize(8);
  doc.setTextColor(...mutedText);
  doc.text(
    "QikTruck Logistics (Pty) Ltd  |  472 Spionkop Avenue, Kya Sands, Johannesburg 2163  |  +27 66 378 4460  |  support@qiktruck.co.za",
    m,
    y
  );

  y += 8;
  const detailLines = [email, promocode ? `Promo: ${promocode}` : ""].filter(Boolean);
  const boxHeight = 14 + detailLines.length * 5.5;

  doc.setFillColor(...lightBg);
  doc.setDrawColor(220, 220, 220);
  doc.setLineWidth(0.3);
  doc.rect(m, y, pw - 2 * m, boxHeight, "FD");

  doc.setFontSize(7);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...mutedText);
  doc.text("INVOICE TO", m + 4, y + 5.5);

  doc.setFontSize(11);
  doc.setTextColor(...darkText);
  doc.text(customerName, m + 4, y + 12);

  doc.setFontSize(8);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(...mutedText);
  let lineY = y + 18;
  detailLines.forEach((line) => {
    doc.text(line, m + 4, lineY);
    lineY += 5.5;
  });

  doc.setFontSize(8);
  doc.text("Period", pw - m - 4, y + 7, { align: "right" });
  doc.setFontSize(9);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...darkText);
  doc.text(periodLabel, pw - m - 4, y + 14, { align: "right" });

  y += boxHeight + 8;
  const first = list[0] || {};
  const truncate = (str, len) =>
    str ? (str.length > len ? str.substring(0, len) + "…" : str) : "-";
  const tripCount = list.length;

  autoTable(doc, {
    startY: y,
    head: [["Vehicle type", "Order Description", "Collection", "Delivery", "Total"]],
    body: [
      [
        { content: "Delivery Services", styles: { fontStyle: "bold" } },
        {
          content: `${tripCount} trip${tripCount !== 1 ? "s" : ""}`,
          styles: { textColor: mutedText },
        },
        truncate(first.collectionaddress || "", 24),
        truncate(first.deliveryaddress || "", 24),
        {
          content: fmtNum(tripsSubtotal),
          styles: { halign: "right", fontStyle: "bold" },
        },
      ],
      [
        {
          content:
            "Service fee (Logistics Administration) - included in trip prices",
          colSpan: 4,
          styles: { textColor: mutedText, fontStyle: "italic", fontSize: 7.5 },
        },
        {
          content: fmtNum(serviceFeeSubtotal),
          styles: {
            halign: "right",
            textColor: mutedText,
            fontStyle: "italic",
            fontSize: 7.5,
          },
        },
      ],
      [
        { content: "Account Fee (SaaS)", colSpan: 4 },
        { content: fmtNum(accountFee), styles: { halign: "right" } },
      ],
    ],
    theme: "grid",
    headStyles: {
      fillColor: primary,
      textColor: [255, 255, 255],
      fontSize: 8.5,
      fontStyle: "bold",
      lineWidth: 0,
      cellPadding: { top: 4, bottom: 4, left: 3, right: 3 },
    },
    bodyStyles: {
      fontSize: 8,
      textColor: darkText,
      lineWidth: 0.2,
      lineColor: [210, 215, 215],
      cellPadding: { top: 3, bottom: 3, left: 3, right: 3 },
    },
    alternateRowStyles: { fillColor: [252, 252, 252] },
    columnStyles: {
      0: { cellWidth: 22 },
      1: { cellWidth: "auto" },
      2: { cellWidth: 36 },
      3: { cellWidth: 36 },
      4: { halign: "right", cellWidth: 26 },
    },
    margin: { left: m, right: m },
  });

  const afterTable = doc.lastAutoTable?.finalY ?? y + 60;
  const summaryLeft = pw - m - 90;

  autoTable(doc, {
    startY: afterTable + 6,
    body: [
      [
        {
          content: "Sub-total (excl. VAT)",
          styles: { textColor: mutedText, fontStyle: "normal" },
        },
        {
          content: fmtNum(subTotalExclVat),
          styles: { halign: "right", textColor: darkText, fontStyle: "normal" },
        },
      ],
      [
        {
          content: "Total VAT at 15%",
          styles: { textColor: mutedText, fontStyle: "normal" },
        },
        {
          content: fmtNum(totalVat),
          styles: { halign: "right", textColor: darkText, fontStyle: "bold" },
        },
      ],
      [
        {
          content: "VAT on service fee (embedded in trips)",
          styles: { textColor: mutedText, fontSize: 7, fontStyle: "italic" },
        },
        {
          content: fmtNum(vatInTrips),
          styles: {
            halign: "right",
            textColor: mutedText,
            fontSize: 7,
            fontStyle: "italic",
          },
        },
      ],
      [
        {
          content: `VAT on account fee (15% of R${fmtNum(accountFee)})`,
          styles: { textColor: mutedText, fontSize: 7, fontStyle: "italic" },
        },
        {
          content: fmtNum(accountFeeVat),
          styles: {
            halign: "right",
            textColor: mutedText,
            fontSize: 7,
            fontStyle: "italic",
          },
        },
      ],
    ],
    theme: "plain",
    tableWidth: 90,
    margin: { left: summaryLeft, right: m },
    styles: {
      fontSize: 8,
      cellPadding: { top: 2.5, bottom: 2.5, left: 2, right: 2 },
      lineWidth: 0,
      font: "helvetica",
    },
    columnStyles: {
      0: { cellWidth: 65 },
      1: { cellWidth: 25 },
    },
  });

  let tY = (doc.lastAutoTable?.finalY ?? afterTable + 40) + 4;

  doc.setFillColor(...yellow);
  doc.rect(m, tY, pw - 2 * m, 16, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setTextColor(...primary);
  doc.text("TOTAL PAYABLE (incl. VAT)", m + 5, tY + 10);
  doc.setFontSize(14);
  doc.text("R " + fmtNum(total), pw - m - 5, tY + 10, { align: "right" });

  tY += 26;
  doc.setFontSize(10);
  doc.setTextColor(...darkText);
  doc.text("PAYMENTS", m, tY);
  tY += 7;

  [
    ["Bank name:", BANK.name],
    ["Branch code:", BANK.branchCode],
    ["Account number:", BANK.accountNumber],
    ["Ref:", invoiceNumber],
  ].forEach(([label, val]) => {
    doc.setFontSize(8.5);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...mutedText);
    doc.text(label, m, tY);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...darkText);
    doc.text(val, m + 36, tY);
    tY += 5.5;
  });

  tY += 6;
  doc.setFontSize(7.5);
  doc.setFont("helvetica", "italic");
  doc.setTextColor(...mutedText);
  doc.text(
    "This is a valid tax invoice in terms of Section 20 of the Value-Added Tax Act 89 of 1991.",
    m,
    tY
  );
  doc.text("Supplier VAT Reg. No.: 4700316260", m, tY + 5);

  return doc;
}
